import React, { useState } from "react";
import { useRegionalManagers } from "../../../apollo/propertyManagers/queries";
import { Grid, Button, Typography } from "@material-ui/core";
import DescriptionCard from "./DescriptionCard";
import { AddRounded } from "@material-ui/icons";
import FormModal from "./FormModal";
import { PropertyManager } from "../../../apollo/propertyManagers/interfaces";
import DeleteModal from "../../../shared/components/DeleteModal";
import { DELETE_REGIONAL_MANAGER } from "../../../apollo/propertyManagers/mutations";
import useAbility from "../../../shared/hooks/useAbility";

const useRegionalManagerSection = (subTenantId: string | null) => {
  const { managers, loading, error, refetch } = useRegionalManagers(subTenantId);
  const ability = useAbility();
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [
    selectedEditItem,
    setSelectedEditItem,
  ] = useState<null | PropertyManager>(null);
  const onCreateClick = () => {
    setSelectedEditItem(null);
    setShowFormModal(true);
  };
  const onCloseFormModalClick = () => {
    setShowFormModal(false);
  };

  const onSubmitSuccess = () => {
    refetch();
    setShowFormModal(false);
  };

  const onDeleteSuccess = () => {
    refetch();
    setShowDeleteModal(false);
  };

  const onDeleteClick = (item: PropertyManager) => {
    setSelectedEditItem(item);
    setShowDeleteModal(true);
  };

  const onDeleteFormModalClose = () => {
    setShowDeleteModal(false);
  };

  const onEditClick = (item: PropertyManager) => {
    setSelectedEditItem(item);
    setShowFormModal(true);
  };
  return {
    model: {
      loading,
      managers,
      showFormModal,
      selectedEditItem,
      showDeleteModal,
      ability,
    },
    commands: {
      onDeleteClick,
      onCreateClick,
      onDeleteFormModalClose,
      onCloseFormModalClick,
      onEditClick,
      onSubmitSuccess,
      onDeleteSuccess,
    },
  };
};
interface RegionalManagerSectionProps {
  subTenantId: string | null;
}
const RegionalManagerSection: React.FC<RegionalManagerSectionProps> = ({subTenantId}) => {
  const {
    model: {
      loading,
      managers,
      showFormModal,
      selectedEditItem,
      showDeleteModal,
      ability,
    },
    commands: {
      onCloseFormModalClick,
      onCreateClick,
      onEditClick,
      onSubmitSuccess,
      onDeleteClick,
      onDeleteFormModalClose,
      onDeleteSuccess,
    },
  } = useRegionalManagerSection(subTenantId);
  const isEmpty = managers && !managers.length;

  if (loading) {
    return <div>loading...</div>;
  }

  return (
    <>
      <FormModal
        open={showFormModal}
        type="manager"
        onClose={onCloseFormModalClick}
        item={selectedEditItem}
        onSuccess={onSubmitSuccess}
        renderTenantSelect={ability.can("manage", "all")}
        subTenantId={subTenantId}
      />
      <DeleteModal
        open={showDeleteModal}
        mutation={DELETE_REGIONAL_MANAGER}
        ids={selectedEditItem ? [selectedEditItem.id] : []}
        description="Are you sure you want to delete this Regional Manager? This cannot be undone."
        title={"Delete Regional Manager"}
        onSuccess={onDeleteSuccess}
        onClose={onDeleteFormModalClose}
      />
      {ability.can("create", "PropertyManagers") && (
        <Button
          variant="contained"
          onClick={onCreateClick}
          color="primary"
          startIcon={<AddRounded />}
        >
          Add New
        </Button>
      )}

      {isEmpty ? (
        <Typography
          variant="body1"
          color="textSecondary"
          style={{ marginTop: 10 }}
        >
          No regional managers have been created.
        </Typography>
      ) : (
        <Grid
          container
          spacing={5}
          style={{ marginTop: 10 }}
          alignItems="stretch"
        >
          {managers?.map((manager) => (
            <Grid key={manager.id} item xs={12} sm={6} md={3}>
              <DescriptionCard
                item={manager}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                canEdit={ability.can('update', 'PropertyManagers')}
                canDelete={ability.can('delete', 'PropertyManagers')}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default RegionalManagerSection;
